import {
  getRgObjectsOfType,
  RgDataSetID,
  RG_TYPE,
  type Apartment,
  type Building,
  type Module,
} from "@project-rouge/rg-core";
import { SvgPlan, SvgStylePresets } from "@project-rouge/rg-three";
import { when } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { DesignsGenerator } from "src/types/DesignsGenerator";
import { CreateBoxWorld } from "src/utils/CreateBoxWorld";
import { RgWorldFromZip } from "src/utils/RgWorldFromZip";
import {
  buildModuleCatalog,
  getModuleMeta,
  modulesPerLevel,
  TESSELLATION_EXAMPLE,
  type ModuleEntry,
} from "src/screens/module-explorer-data";

// Admin screen for understanding how individual building Modules are shaped
// and how they tessellate into a building. Two sections:
//   A) Catalog — every module in a dataset's apartments, drawn as a 2D plan.
//   B) Tessellation — one generated building, drawn as a plan so the module
//      grid is legible.
// Reuses SvgPlan (rg-three) for all rendering; no offscreen/3D path.

const DATASETS: RgDataSetID[] = [RgDataSetID.uk_01, RgDataSetID.uk_02];
const PLAN_STYLES = SvgStylePresets.floorPlan;

type ExampleStatus = "pending" | "generating" | "done" | "error";

export const ModuleExplorerScreen = observer(function ModuleExplorerScreen() {
  const [datasetId, setDatasetId] = useState<RgDataSetID>(RgDataSetID.uk_01);
  const [selected, setSelected] = useState<ModuleEntry | null>(null);

  const catalog = useMemo(() => buildModuleCatalog(datasetId), [datasetId]);

  return (
    <div className="h-screen w-screen overflow-y-auto bg-white text-neutral-8 p-6 font-mono text-xs">
      <header className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-lg font-bold mr-4">Module explorer</h1>
        <div className="flex gap-1">
          {DATASETS.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setDatasetId(id)}
              className={`px-3 py-1 rounded ${
                id === datasetId
                  ? "bg-neutral-8 text-white"
                  : "bg-neutral-3 text-neutral-7 hover:bg-neutral-4"
              }`}
            >
              {id}
            </button>
          ))}
        </div>
        <span className="text-neutral-6 ml-auto">{catalog.length} modules</span>
      </header>

      <p className="text-neutral-6 mb-4">
        Each apartment in a dataset is built from one or more structural modules. Section A
        shows every module on its own (2D plan: walls, doors, windows). Section B generates one
        building so you can see how modules tessellate. <code>uk_02</code> is grid-based;{" "}
        <code>uk_01</code> uses variable widths.
      </p>

      <h2 className="text-sm font-bold mb-2 border-b border-neutral-3 pb-1">A · Catalog</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
        {catalog.map((entry) => (
          <ModuleTile
            key={`${entry.apartmentName}-${entry.moduleIndex}`}
            entry={entry}
            onClick={() => setSelected(entry)}
          />
        ))}
      </div>

      <h2 className="text-sm font-bold mb-2 border-b border-neutral-3 pb-1">
        B · Tessellation example
      </h2>
      <TessellationSection />

      {selected && <ModuleModal entry={selected} onClose={() => setSelected(null)} />}
    </div>
  );
});

function ModuleTile({ entry, onClick }: { entry: ModuleEntry; onClick: () => void }) {
  const meta = useMemo(() => getModuleMeta(entry.module), [entry.module]);
  return (
    <button
      type="button"
      onClick={onClick}
      className="border border-neutral-3 rounded p-2 flex flex-col text-left hover:border-neutral-6"
    >
      <div className="aspect-square bg-neutral-1 border border-neutral-2 rounded mb-2 overflow-hidden">
        <SvgPlan obj={entry.module} styles={PLAN_STYLES} showScale />
      </div>
      <span className="font-semibold truncate" title={entry.apartmentName}>
        {entry.apartmentName}
      </span>
      <span className="text-neutral-6">
        {entry.category} · module {entry.moduleIndex + 1}
      </span>
      <span className="text-neutral-5">
        {fmtM(meta.width)} × {fmtM(meta.depth)} m
      </span>
    </button>
  );
}

function ModuleModal({ entry, onClose }: { entry: ModuleEntry; onClose: () => void }) {
  const meta = useMemo(() => getModuleMeta(entry.module), [entry.module]);
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-8 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-3xl w-full max-h-full overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-base font-bold">
            {entry.apartmentName} · module {entry.moduleIndex + 1}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="px-2 py-1 rounded bg-neutral-3 hover:bg-neutral-4"
          >
            close
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="aspect-square bg-neutral-1 border border-neutral-2 rounded overflow-hidden">
            <SvgPlan obj={entry.module} styles={PLAN_STYLES} showScale />
          </div>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 self-start">
            <Row label="Category" value={entry.category} />
            <Row label="Dataset" value={entry.datasetId} />
            <Row label="Width" value={`${fmtM(meta.width)} m`} />
            <Row label="Height" value={`${fmtM(meta.height)} m`} />
            <Row label="Depth" value={`${fmtM(meta.depth)} m`} />
            <Row label="Walls" value={`${meta.wallCount}`} />
            <Row label="Doors" value={`${meta.doorCount}`} />
            <Row label="Windows" value={`${meta.windowCount}`} />
            <Row label="Beams" value={`${meta.beamCount}`} />
            <Row label="Columns" value={`${meta.columnCount}`} />
          </dl>
        </div>
        <h4 className="text-sm font-bold mt-6 mb-2 border-t border-neutral-3 pt-3">
          Parts (modulous categories)
        </h4>
        {Object.keys(meta.parts).length === 0 ? (
          <p className="text-neutral-5 italic">No modulousId-tagged children.</p>
        ) : (
          <dl className="grid grid-cols-3 md:grid-cols-5 gap-x-3 gap-y-1">
            {Object.entries(meta.parts)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([code, count]) => (
                <Row key={code} label={code} value={`${count}`} />
              ))}
          </dl>
        )}
      </div>
    </div>
  );
}

function TessellationSection() {
  const [status, setStatus] = useState<ExampleStatus>("pending");
  const [building, setBuilding] = useState<Building | null>(null);
  const [perLevel, setPerLevel] = useState<Record<number, number>>({});
  const [moduleTotal, setModuleTotal] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    const generator = new DesignsGenerator({ projectId: "module-explorer", listSize: 1 });
    (async () => {
      setStatus("generating");
      try {
        const { length, width, floors } = TESSELLATION_EXAMPLE;
        generator.generate(CreateBoxWorld(length, width, floors));
        await new Promise<void>((resolve) =>
          when(
            () => !generator.isRunning || generator.list.length > 0,
            () => resolve()
          )
        );
        if (cancelledRef.current) return;
        const item = generator.list[0];
        if (!item) {
          console.error("[module-explorer] generator produced no option");
          setStatus("error");
          return;
        }
        const rgWorld = RgWorldFromZip(item.rgWorldZip);
        const buildings = getRgObjectsOfType<Building>(rgWorld, RG_TYPE.Building);
        const apartments = getRgObjectsOfType<Apartment>(rgWorld, RG_TYPE.Apartment);
        const modules = getRgObjectsOfType<Module>(rgWorld, RG_TYPE.Module);
        setBuilding(buildings[0] ?? null);
        setPerLevel(modulesPerLevel(apartments));
        setModuleTotal(modules.length);
        setStatus("done");
      } catch (err) {
        console.error("[module-explorer] tessellation generation failed", err);
        if (!cancelledRef.current) setStatus("error");
      }
    })();
    return () => {
      cancelledRef.current = true;
      generator.terminate();
    };
  }, []);

  if (status !== "done") {
    return (
      <p className="text-neutral-5 italic">
        {status === "error" ? "Generation failed." : "Generating building…"}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="aspect-square bg-neutral-1 border border-neutral-2 rounded overflow-hidden">
        {building && <SvgPlan obj={building} styles={PLAN_STYLES} showScale />}
      </div>
      <div className="self-start">
        <p className="text-neutral-6 mb-2">
          {TESSELLATION_EXAMPLE.datasetId} · {TESSELLATION_EXAMPLE.length}×
          {TESSELLATION_EXAMPLE.width}m · {TESSELLATION_EXAMPLE.floors} floors
        </p>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 max-w-xs">
          <Row label="Total modules" value={`${moduleTotal}`} />
          {Object.entries(perLevel)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([level, count]) => (
              <Row key={level} label={`Level ${level}`} value={`${count}`} />
            ))}
        </dl>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-neutral-6">{label}</dt>
      <dd className="text-right tabular-nums">{value}</dd>
    </>
  );
}

function fmtM(value: number): string {
  return value.toFixed(2);
}
